import Controls from '@/components/game/Controls';
import GameBoard3D from '@/components/game3d/GameBoard3D';
import HUD3D from '@/components/game3d/HUD3D';
import LoadingScreen3D from '@/components/game3d/LoadingScreen3D';
import ModalTheme3D from '@/components/game3d/ModalTheme3D';
import PowerupDisplay from '@/components/game/PowerupDisplay';
import Timer from '@/components/game/Timer';
import TouchControls from '@/components/game/TouchControls';
import Commentary from '@/components/game/Commentary';
import CardHand from '@/components/game/CardHand';
import CardPicker from '@/components/game/CardPicker';
import CardPlayEffect from '@/components/game/CardPlayEffect';
import SettingsMenuWrapper from '@/components/layout/SettingsMenuWrapper';
import type { LevelCompleteStats } from '@/components/modals';
import {
  DailyRewardModal,
  GameOverModal,
  LevelCompleteModal,
  LevelSelectModal,
  markObstacleTutorialShown,
  NoLivesModal,
  ObstacleTutorialModal,
  ShopModal,
} from '@/components/modals';
import { useShop } from '@/contexts/ShopContext';
import { useSound } from '@/contexts/SoundContext';
import { puzzles } from '@/game/puzzles';
import { getUpgradeManager } from '@/game/upgrades';
import { useGameEngine, getSavedInfiniteRunState, clearInfiniteRunState } from '@/hooks/useGameEngine';
import { useCardSystem } from '@/hooks/useCardSystem';
import { useKeyboardControls } from '@/hooks/useKeyboardControls';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useModal } from '@/hooks/useModal';
import { useFocusManager } from '@/hooks/useFocusManager';
import { StorageService } from '@/services/storage';
import { getCommentarySystem } from '@/game/commentarySystem';
import { CARD_DEFINITIONS } from '@/config/cards';
import type { CardType, CardRarity } from '@/types/card';
import type { Direction } from '@/types/game';
import { motion, AnimatePresence } from 'motion/react';
import { useEffect, useRef, useState, useCallback } from 'react';
import { getWorldForLevel } from '@/config/worlds';

interface Game3DScreenProps {
  onOpenSettings: () => void;
  onGoHome: () => void;
  onOpenShop?: () => void;
  onToggleStats?: () => void;
  onLevelSelect?: () => void;
}

function Game3DScreen({
  onOpenSettings: _onOpenSettings,
  onGoHome,
  onOpenShop = () => {},
  onToggleStats = () => {},
  onLevelSelect = () => {},
}: Game3DScreenProps) {
  const { gameState, movePlayer, restartLevel, loadLevel, loadInfiniteWave, loadNextLevel, hintPath, hintPathHazards, triggerLevelCompleteModal, restartCounter, setPaused, playCardEffect, warpToPosition } = useGameEngine();
  const { activeModal, openModal, closeModal, isModalOpen } = useModal();
  const isMobile = useIsMobile();
  const { shopState, shopManager } = useShop();
  const { playGameOver, playCardDraw, playCardPlay, playLevelStart, startWorldMusic, stopWorldMusic } = useSound();
  const cardSystem = useCardSystem();

  // Level transition overlay state
  const [showTransition, setShowTransition] = useState(false);
  const [transitionLevel, setTransitionLevel] = useState<number | null>(null);
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Expose text mode snapshot for automated testing
  const gameStateRef = useRef(gameState);
  gameStateRef.current = gameState;
  const shopStateRef = useRef(shopState);
  shopStateRef.current = shopState;
  const shopManagerRef = useRef(shopManager);
  shopManagerRef.current = shopManager;
  const uiStateRef = useRef({
    activeModal: null as any,
    levelCompleteModalOpen: false,
    obstacleModalOpen: false,
    gameOverModalOpen: false,
    showNoLivesModal: false,
    showSettingsModal: false,
    isMovementLocked: false,
  });
  useEffect(() => {
    (window as any).render_game_to_text = () => {
      const s = gameStateRef.current;
      const remainingCollectibles = (s.collectibles || []).filter(c => !c.isCollected);

      return JSON.stringify({
        mode: 'game3d',
        route: `${window.location.pathname}${window.location.search}`,
        coords: { format: '[col,row]', origin: 'top-left', x: 'right', y: 'down' },
        level: s.level,
        isTutorial: Boolean(s.isTutorial),
        isPlaying: s.isPlaying,
        isPaused: s.isPaused,
        player: { col: s.position[0], row: s.position[1] },
        end: { col: s.end[0], row: s.end[1] },
        stats: { stepsTaken: s.gameStats.stepsTaken, score: s.gameStats.score, streak: s.gameStats.streak },
        wallet: {
          coins: shopStateRef.current.coins,
          gems: shopStateRef.current.gems,
          lives: shopStateRef.current.lives,
          maxLives: shopManagerRef.current?.getMaxLives?.() ?? 5,
        },
        collectibles: {
          total: (s.collectibles || []).length,
          remaining: remainingCollectibles.length,
          collectedInLevel: s.collectedInLevel,
        },
        collision: { hasCollided: s.hasCollided },
        metaUpgrades: getUpgradeManager().getState().levels,
        ui: uiStateRef.current,
      });
    };

    return () => {
      try { delete (window as any).render_game_to_text; } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clean up transition timer on unmount
  useEffect(() => {
    return () => {
      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
    };
  }, []);

  // Focus management (handled by useFocusManager call below with pause/resume handlers)

  // Request fullscreen when entering game (user gesture required on some browsers)
  useEffect(() => {
    const requestFullscreen = () => {
      const el = document.documentElement;
      if (!document.fullscreenElement && el.requestFullscreen) {
        el.requestFullscreen().catch(() => { /* user gesture may be required */ });
      }
    };
    // Try immediately for PWA context, and also on first user interaction
    requestFullscreen();
    const handler = () => { requestFullscreen(); window.removeEventListener('touchstart', handler); window.removeEventListener('click', handler); };
    window.addEventListener('touchstart', handler, { once: true });
    window.addEventListener('click', handler, { once: true });
    return () => { window.removeEventListener('touchstart', handler); window.removeEventListener('click', handler); };
  }, []);

  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Level complete modal state
  const [levelCompleteModalOpen, setLevelCompleteModalOpen] = useState(false);
  const [levelWon, setLevelWon] = useState(false);
  const [levelCompleteStats, setLevelCompleteStats] = useState<LevelCompleteStats | null>(null);

  // Obstacle tutorial state
  const [obstacleModalOpen, setObstacleModalOpen] = useState(false);
  const [currentObstacle, setCurrentObstacle] = useState<string | null>(null);
  const currentObstacleRef = useRef<string | null>(null);
  currentObstacleRef.current = currentObstacle;

  // Game over modal state
  const [gameOverModalOpen, setGameOverModalOpen] = useState(false);
  const [gameOverStats, setGameOverStats] = useState<{ level: number; score: number; steps: number } | null>(null);

  // Tutorial instructions state
  const [instructionsText, setInstructionsText] = useState('');

  // Game pause/focus management
  const handleGamePause = useCallback(() => {
    if (gameState.isPlaying && !gameState.isPaused) {
      openModal('settings');
    }
  }, [gameState.isPlaying, gameState.isPaused, openModal]);

  const handleGameResume = useCallback(() => {}, []);

  useFocusManager({
    onFocusLost: handleGamePause,
    onFocusGained: handleGameResume,
    pauseOnFocusLoss: true,
    debounceMs: 150,
  });

  // No lives modal state
  const [showNoLivesModal, setShowNoLivesModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [hudExpanded, setHudExpanded] = useState(false);

  const handleHudToggle = useCallback(() => {
    setHudExpanded(prev => {
      const next = !prev;
      setPaused(next);
      return next;
    });
  }, [setPaused]);

  // Daily reward modal
  const [showDailyReward, setShowDailyReward] = useState(false);
  useEffect(() => {
    if (!hasInitialized) return;
    const status = shopManager.getDailyRewardStatus();
    if (status.canClaim) {
      const timer = setTimeout(() => setShowDailyReward(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [hasInitialized, shopManager]);

  // Card picker state
  const [showCardPicker, setShowCardPicker] = useState(false);
  const [playedCard, setPlayedCard] = useState<{ type: CardType; rarity: CardRarity } | null>(null);
  const playedCardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (gameState.isPlaying && cardSystem.inventory.length > 0 && cardSystem.hand.length === 0) {
      setShowCardPicker(true);
      setPaused(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState.level, gameState.isPlaying]);

  const handleCardPickerConfirm = useCallback((selectedIds: string[]) => {
    cardSystem.selectHand(selectedIds);
    setShowCardPicker(false);
    setPaused(false);
  }, [cardSystem, setPaused]);

  const handleCardPickerSkip = useCallback(() => {
    setShowCardPicker(false);
    setPaused(false);
  }, [setPaused]);

  const handlePlayCard = useCallback((cardId: string) => {
    const cardInfo = cardSystem.hand.find(c => c.id === cardId);
    const cardType = cardSystem.playCard(cardId);
    if (cardType) {
      playCardPlay();
      playCardEffect(cardType);

      const rarity = cardInfo?.rarity ?? CARD_DEFINITIONS[cardType].rarity;
      setPlayedCard({ type: cardType, rarity });
      if (playedCardTimerRef.current) clearTimeout(playedCardTimerRef.current);
      playedCardTimerRef.current = setTimeout(() => setPlayedCard(null), 1200);

      const commentary = getCommentarySystem();
      const messages: Record<CardType, string> = {
        freeze: 'Freeze Card played! Enemies frozen for 2 turns',
        scout: 'Scout Card played! Path to exit revealed',
        shield: 'Shield Card played! Protected from next hit',
        warp: 'Warp Card played! Select a tile to teleport',
        rewind: 'Rewind Card played! Rewinding 3 moves',
      };
      commentary.trigger(messages[cardType], 'excited', 'high');
    }
  }, [cardSystem, playCardEffect, playCardPlay]);

  // Movement lock state
  const [isMovementLocked, setIsMovementLocked] = useState(false);

  uiStateRef.current = {
    activeModal: activeModal ?? null,
    levelCompleteModalOpen,
    obstacleModalOpen,
    gameOverModalOpen,
    showNoLivesModal,
    showSettingsModal,
    isMovementLocked,
  };

  // Event listeners
  useEffect(() => {
    const handleGameOver = (event: any) => {
      const { level, score, steps } = event.detail;
      setGameOverStats({ level, score, steps });
      setGameOverModalOpen(true);
      playGameOver();
    };
    window.addEventListener('gameOver', handleGameOver);
    return () => window.removeEventListener('gameOver', handleGameOver);
  }, [playGameOver]);

  useEffect(() => {
    if (shopState.lives <= 0 && hasInitialized && !showNoLivesModal) {
      setShowNoLivesModal(true);
    }
  }, [shopState.lives, hasInitialized, showNoLivesModal]);

  useEffect(() => {
    const handleNewObstacle = (event: any) => {
      const { obstacle } = event.detail;
      const alreadyShown = localStorage.getItem(`obstacle_tutorial_${obstacle}`);
      if (alreadyShown) return;
      setCurrentObstacle(obstacle);
      setObstacleModalOpen(true);
    };
    window.addEventListener('newObstacle', handleNewObstacle);
    return () => window.removeEventListener('newObstacle', handleNewObstacle);
  }, []);

  useEffect(() => {
    const handleLevelComplete = (event: any) => {
      const { score, stars, completionTime, steps, retries, keyPressSpeed, collectiblesCollected, collectiblesTotal } = event.detail;
      setLevelCompleteStats({
        stepsTaken: steps,
        timeSpent: completionTime,
        retries: retries ?? 0,
        keyPressSpeed: keyPressSpeed ?? 0,
        stars,
        score,
        collectiblesCollected: collectiblesCollected ?? 0,
        collectiblesTotal: collectiblesTotal ?? 0,
      });
      setLevelWon(true);
      setLevelCompleteModalOpen(true);

      const droppedCard = cardSystem.rollDrop();
      if (droppedCard) {
        cardSystem.addToInventory(droppedCard);
        playCardDraw();
        const cardTutorialKey = `obstacle_tutorial_card_${droppedCard.type}`;
        if (!localStorage.getItem(cardTutorialKey)) {
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('newObstacle', { detail: { obstacle: `card_${droppedCard.type}` } }));
          }, 2000);
        }
      }
    };
    window.addEventListener('levelComplete', handleLevelComplete);
    return () => window.removeEventListener('levelComplete', handleLevelComplete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardSystem, playCardDraw]);

  useEffect(() => {
    const handleAllLevelsComplete = () => { onGoHome(); };
    window.addEventListener('allLevelsComplete', handleAllLevelsComplete);
    return () => window.removeEventListener('allLevelsComplete', handleAllLevelsComplete);
  }, [onGoHome]);

  useEffect(() => {
    setInstructionsText(isMobile ? 'Swipe in any direction to move the player' : 'Use the arrow keys to move the player');
  }, [isMobile]);

  // Initialize level: load silently during loading screen, play sounds after
  useEffect(() => {
    if (hasInitialized) return;
    const urlParams = new URLSearchParams(window.location.search);
    const levelParam = urlParams.get('level');
    const modeParam = urlParams.get('mode');

    // Determine target level for sound scheduling
    let targetLevel = 0;

    const loadTimeout = setTimeout(() => {
      if (modeParam === 'infinite') {
        const freshParam = urlParams.get('fresh');
        if (freshParam === '1') {
          clearInfiniteRunState();
          loadInfiniteWave(1, undefined, { silent: true });
        } else {
          const saved = getSavedInfiniteRunState();
          if (saved) loadInfiniteWave(saved.wave, saved, { silent: true });
          else loadInfiniteWave(1, undefined, { silent: true });
        }
      } else {
        if (levelParam) {
          const level = parseInt(levelParam, 10);
          if (!isNaN(level) && level >= 0 && level < puzzles.length) targetLevel = level;
        }
        loadLevel(targetLevel, { silent: true });
        StorageService.saveLastPlayedLevel(targetLevel);
      }

      if (shopState.lives <= 0) setShowNoLivesModal(true);

      // Keep loading screen a bit longer so 3D scene settles, then play sounds
      setTimeout(() => {
        setIsLoading(false);
        setHasInitialized(true);

        // Play level start sound and world music now that scene is ready
        if (modeParam === 'infinite') {
          const worldIds = ['greenwood', 'stonehollow', 'frostline', 'emberdeep', 'arcana'] as const;
          playLevelStart();
          startWorldMusic(worldIds[0]);
        } else {
          const world = getWorldForLevel(targetLevel);
          playLevelStart();
          startWorldMusic(world.id);
        }
      }, 800);
    }, 100);

    return () => clearTimeout(loadTimeout);
  }, [loadLevel, loadInfiniteWave, hasInitialized, shopState.lives]);

  const [_hasShownCompleteModal, setHasShownCompleteModal] = useState(false);

  useEffect(() => {
    setHasShownCompleteModal(false);
  }, [gameState.level, restartCounter]);

  // URL-based level changes
  useEffect(() => {
    if (!hasInitialized) return;
    const handleLocationChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const levelParam = urlParams.get('level');
      if (levelParam) {
        const newLevel = parseInt(levelParam, 10);
        if (!isNaN(newLevel) && newLevel >= 0 && newLevel < puzzles.length && newLevel !== gameState.level) {
          setIsLoading(true);
          stopWorldMusic();
          setTimeout(() => {
            loadLevel(newLevel, { silent: true });
            setTimeout(() => {
              setIsLoading(false);
              const world = getWorldForLevel(newLevel);
              playLevelStart();
              startWorldMusic(world.id);
            }, 600);
          }, 100);
        }
      }
    };
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, [gameState.level, loadLevel, hasInitialized]);

  const handleMove = useCallback((direction: Direction) => {
    if (showNoLivesModal) return;
    movePlayer(direction);
  }, [showNoLivesModal, movePlayer]);

  const handlePowerup = (_slot: number) => {};

  const handleLevelCompleteClose = useCallback(() => {
    setLevelCompleteModalOpen(false);
    setLevelCompleteStats(null);
  }, []);

  const handleContinueToNextLevel = useCallback(() => {
    handleLevelCompleteClose();
    if (shopState.lives <= 0) { setShowNoLivesModal(true); return; }
    cardSystem.returnHandToInventory();

    // Determine the next level for the transition screen
    const nextLevel = gameState.gameMode === 'endless'
      ? (gameState.infiniteState?.wave ?? 0)
      : gameState.level + 1;

    // Compute the next world BEFORE loading (avoids stale closure after loadNextLevel)
    const nextWorldId = getWorldForLevel(
      gameState.gameMode === 'endless' ? 0 : nextLevel
    ).id;

    // Show transition, stop current music
    stopWorldMusic();
    setTransitionLevel(nextLevel);
    setShowTransition(true);

    // After a brief transition pause, load the next level silently
    if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
    transitionTimerRef.current = setTimeout(() => {
      loadNextLevel({ silent: true });

      // After scene has settled, dismiss transition and start sounds
      transitionTimerRef.current = setTimeout(() => {
        setShowTransition(false);
        setTransitionLevel(null);

        // Now play level start sound and world music (using pre-captured world ID)
        playLevelStart();
        startWorldMusic(nextWorldId);
      }, 600);
    }, 500);
  }, [handleLevelCompleteClose, shopState.lives, cardSystem, gameState, stopWorldMusic, loadNextLevel, playLevelStart, startWorldMusic]);

  const handleRetryLevel = useCallback(() => {
    setLevelCompleteModalOpen(false);
    setLevelCompleteStats(null);
    setLevelWon(false);
    setHasShownCompleteModal(false);
    if (shopState.lives <= 0) { setShowNoLivesModal(true); return; }
    cardSystem.returnHandToInventory();

    // Show brief transition for retry
    stopWorldMusic();
    setTransitionLevel(gameState.level);
    setShowTransition(true);

    if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
    transitionTimerRef.current = setTimeout(() => {
      restartLevel();

      transitionTimerRef.current = setTimeout(() => {
        setShowTransition(false);
        setTransitionLevel(null);
      }, 400);
    }, 400);
  }, [shopState.lives, cardSystem, gameState.level, stopWorldMusic, restartLevel]);

  const handleObstacleTutorialClose = useCallback(() => {
    const obs = currentObstacleRef.current;
    if (obs) markObstacleTutorialShown(obs);
    setObstacleModalOpen(false);
  }, []);

  const handleLevelSelect = useCallback((levelIndex: number) => {
    closeModal();
    if (shopState.lives <= 0) { setShowNoLivesModal(true); return; }

    // Show transition for level selection
    stopWorldMusic();
    setTransitionLevel(levelIndex);
    setShowTransition(true);

    if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
    transitionTimerRef.current = setTimeout(() => {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('level', levelIndex.toString());
      window.history.pushState({}, '', newUrl.toString());
      loadLevel(levelIndex, { silent: true });

      transitionTimerRef.current = setTimeout(() => {
        setShowTransition(false);
        setTransitionLevel(null);
        const world = getWorldForLevel(levelIndex);
        playLevelStart();
        startWorldMusic(world.id);
      }, 600);
    }, 500);
  }, [closeModal, shopState.lives, stopWorldMusic, loadLevel, playLevelStart, startWorldMusic]);

  const handleEscape = () => {
    if (activeModal || levelCompleteModalOpen || obstacleModalOpen || gameOverModalOpen || showSettingsModal) {
      if (activeModal) closeModal();
      if (levelCompleteModalOpen) handleLevelCompleteClose();
      if (obstacleModalOpen) handleObstacleTutorialClose();
      if (gameOverModalOpen) setGameOverModalOpen(false);
      if (showSettingsModal) setShowSettingsModal(false);
    } else {
      setShowSettingsModal(true);
    }
  };

  const handleRestart = useCallback(() => {
    if (shopState.lives <= 0) { setShowNoLivesModal(true); return; }
    cardSystem.returnHandToInventory();
    restartLevel();
    if (activeModal) closeModal();
    if (levelCompleteModalOpen) handleLevelCompleteClose();
    if (obstacleModalOpen) handleObstacleTutorialClose();
    if (gameOverModalOpen) setGameOverModalOpen(false);
    if (showSettingsModal) setShowSettingsModal(false);
  }, [shopState.lives, cardSystem, restartLevel, activeModal, closeModal, levelCompleteModalOpen, handleLevelCompleteClose, obstacleModalOpen, handleObstacleTutorialClose, gameOverModalOpen, showSettingsModal]);

  useKeyboardControls({
    onMove: handleMove,
    onRestart: handleRestart,
    onHome: onGoHome,
    onSettings: () => setShowSettingsModal(true),
    onShop: () => {
      if (isMobile) openModal('shop' as any);
      else onOpenShop();
    },
    onStats: onToggleStats,
    onPowerup: handlePowerup,
    onEscape: handleEscape,
    onEnter: () => {},
    onLevelSelect: () => {
      if (isMobile) openModal('levelSelect' as any);
      else onLevelSelect();
    },
    disabled: !gameState.isPlaying || showNoLivesModal || isMovementLocked,
    isHomeScreen: false,
  });

  const currentWorld = getWorldForLevel(gameState.level);

  // Loading screen
  if (isLoading) {
    return <LoadingScreen3D worldId={currentWorld.id} />;
  }

  return (
    <div
      id="game-container"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100dvh',
        overflow: 'hidden',
        background: '#000',
      }}
    >
      {/* 3D Canvas fills the viewport */}
      <GameBoard3D
        gameState={gameState}
        hintPath={hintPath}
        hintPathHazards={hintPathHazards}
        onCellClick={warpToPosition}
        onMovementLockChange={setIsMovementLocked}
        onExitAnimationComplete={triggerLevelCompleteModal}
        restartCounter={restartCounter}
      />

      {/* Level transition overlay */}
      <AnimatePresence>
        {showTransition && (
          <LoadingScreen3D
            worldId={getWorldForLevel(transitionLevel ?? gameState.level).id}
            levelNumber={transitionLevel ?? gameState.level}
            isTransition
          />
        )}
      </AnimatePresence>

      {/* ---- HUD Layer ---- */}
      <HUD3D
        gameState={gameState}
        isExpanded={hudExpanded}
        onToggle={handleHudToggle}
        onOpenSettings={() => setShowSettingsModal(true)}
        onRestart={() => { setHudExpanded(false); setPaused(false); cardSystem.returnHandToInventory(); restartLevel(); }}
        onQuit={() => { setHudExpanded(false); setPaused(false); cardSystem.returnHandToInventory(); onGoHome(); }}
        onPlayCard={handlePlayCard}
        hand={cardSystem.hand}
      />

      {/* ---- Controls Layer (transparent overlay) ---- */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10,
          pointerEvents: 'none',
        }}
      >
        {/* Commentary toast */}
        <div style={{ pointerEvents: 'auto' }}>
          <Commentary />
        </div>

        {/* Tutorial instructions */}
        <AnimatePresence>
          {gameState.isTutorial && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              style={{
                position: 'absolute',
                bottom: '14vh',
                left: 0,
                right: 0,
                textAlign: 'center',
                pointerEvents: 'none',
              }}
            >
              <span style={{
                color: '#fff',
                fontSize: 13,
                fontFamily: 'system-ui, sans-serif',
                background: 'rgba(0,0,0,0.5)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                padding: '8px 16px',
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.1)',
              }}>
                {instructionsText}
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Timer */}
        {!gameState.isTutorial && (
          <div style={{ pointerEvents: 'auto' }}>
            <Timer seconds={0} minutes={0} isRunning={gameState.isPlaying} isSlowed={shopState.powerups.slowTime > 0} />
          </div>
        )}

        {/* Touch / keyboard controls */}
        <div style={{ pointerEvents: 'auto' }}>
          {isMobile ? (
            <TouchControls
              onMove={handleMove}
              disabled={!gameState.isPlaying || showNoLivesModal || isMovementLocked}
              showVisualFeedback={true}
            />
          ) : (
            <Controls onMove={handleMove} />
          )}
        </div>
      </div>

      {/* ---- Powerup Display ---- */}
      <PowerupDisplay
        onUsePowerup={powerupId => {
          const powerupTutorialKey = `obstacle_tutorial_powerup_${powerupId}`;
          if (!localStorage.getItem(powerupTutorialKey)) {
            window.dispatchEvent(new CustomEvent('newObstacle', { detail: { obstacle: `powerup_${powerupId}` } }));
          }
          switch (powerupId) {
            case 'shield': playCardEffect('shield' as any); break;
            case 'invincibility': shopManager.addPowerup('invincibility'); break;
            case 'doubleCoins': shopManager.addPowerup('doubleCoins'); break;
            case 'slowTime': shopManager.addPowerup('slowTime'); break;
            case 'hint': playCardEffect('scout' as any); break;
            case 'undo': playCardEffect('rewind' as any); break;
            case 'skipLevel': loadNextLevel(); break;
            case 'teleport': playCardEffect('warp' as any); break;
          }
        }}
      />

      {/* ---- Card Hand ---- */}
      {gameState.isPlaying && !showCardPicker && cardSystem.hand.length > 0 && (
        <CardHand cards={cardSystem.hand} onPlayCard={handlePlayCard} />
      )}

      {/* Card Play Effect */}
      <CardPlayEffect playedCard={playedCard} />

      {/* Card Picker */}
      {showCardPicker && (
        <CardPicker inventory={cardSystem.inventory} onConfirm={handleCardPickerConfirm} onSkip={handleCardPickerSkip} />
      )}

      {/* ---- Modals (themed for 3D) ---- */}
      <ModalTheme3D>
        <LevelSelectModal isOpen={isModalOpen('levelSelect')} onClose={closeModal} onLevelSelect={handleLevelSelect} currentLevel={gameState.level} />
        <ShopModal isOpen={isModalOpen('shop')} onClose={closeModal} />

        {levelCompleteStats && (
          <LevelCompleteModal
            isOpen={levelCompleteModalOpen}
            levelWon={levelWon}
            title={levelWon ? 'Level Complete!' : 'Level Failed'}
            subtitle={levelWon ? 'Great job! Ready for the next challenge?' : "Don't give up! Try again?"}
            stats={levelCompleteStats}
            onContinue={handleContinueToNextLevel}
            onRetry={handleRetryLevel}
            onClose={handleLevelCompleteClose}
          />
        )}

        <ObstacleTutorialModal isOpen={obstacleModalOpen} obstacleType={currentObstacle} onClose={handleObstacleTutorialClose} />

        {gameOverStats && (
          <GameOverModal
            isOpen={gameOverModalOpen}
            onClose={() => setGameOverModalOpen(false)}
            onRestart={() => { setGameOverModalOpen(false); handleRestart(); }}
            onHome={() => { setGameOverModalOpen(false); cardSystem.returnHandToInventory(); onGoHome(); }}
            onShop={() => { setGameOverModalOpen(false); openModal('shop'); }}
            stats={gameOverStats}
          />
        )}

        <NoLivesModal
          isOpen={showNoLivesModal}
          onClose={() => {
            if (shopState.lives > 0) setShowNoLivesModal(false);
            else onGoHome();
          }}
          onShop={() => {
            setShowNoLivesModal(false);
            if (isMobile) openModal('shop' as any);
            else onOpenShop();
          }}
        />

        <SettingsMenuWrapper
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          onRestart={handleRestart}
          onGoHome={onGoHome}
          onToggleFullscreen={() => {
            if (!document.fullscreenElement) document.documentElement.requestFullscreen();
            else document.exitFullscreen();
          }}
        />

        <DailyRewardModal isOpen={showDailyReward} onClose={() => setShowDailyReward(false)} />
      </ModalTheme3D>
    </div>
  );
}

export default Game3DScreen;
